import React from 'react';
import { motion } from 'framer-motion';
import { Star, Lightning, ShieldCheck, Handshake, Certificate, ArrowRight } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../i18n/translations';

const blueprintImage = "https://static.prod-images.emergentagent.com/jobs/0d77d791-1090-4f5a-a5b4-c78945c5298b/images/051a345166e964fbfe5951386a9423d73ec0720ce6b5a93da83754f860dc857b.png";

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
  viewport: { once: true },
});

export const AboutSection = () => {
  const { t } = useLanguage();

  const values = [
    { icon: Star, title: t.about.values.quality, desc: t.about.values.quality_desc },
    { icon: Lightning, title: t.about.values.innovation, desc: t.about.values.innovation_desc },
    { icon: ShieldCheck, title: t.about.values.reliability, desc: t.about.values.reliability_desc },
    { icon: Handshake, title: t.about.values.integrity, desc: t.about.values.integrity_desc },
  ];

  return (
    <section id="about" className="py-24 lg:py-32 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div {...fadeIn()} className="mb-16">
          <p className="text-[#FF6B00] text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-4">{t.about.title}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1320] tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="about-title">
            {t.about.subtitle}
          </h2>
        </motion.div>

        {/* History + Image */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          <motion.div {...fadeIn(0.1)} className="relative aspect-[4/3] overflow-hidden">
            <img src={blueprintImage} alt="JKM Engineering" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1320]/40 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 bg-white/10 backdrop-blur-xl border border-white/20 p-4">
              <p className="text-white text-sm font-medium">Est. 2009 — Kananga, RDC</p>
            </div>
          </motion.div>
          <div className="space-y-6">
            <motion.div {...fadeIn(0.2)}>
              <h3 className="text-2xl font-bold text-[#0B1320] mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{t.about.history.title}</h3>
              <p className="text-[#475569] leading-relaxed mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>{t.about.history.p1}</p>
              <p className="text-[#475569] leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{t.about.history.p2}</p>
            </motion.div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-6 mb-20">
          <motion.div {...fadeIn(0.1)} className="bg-[#0B1320] p-8 lg:p-10">
            <h3 className="text-[#FF6B00] text-xs font-medium uppercase tracking-[0.2em] mb-4">{t.about.mission.title}</h3>
            <p className="text-white text-lg leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{t.about.mission.text}</p>
          </motion.div>
          <motion.div {...fadeIn(0.2)} className="bg-[#F8FAFC] border border-[#E2E8F0] p-8 lg:p-10">
            <h3 className="text-[#FF6B00] text-xs font-medium uppercase tracking-[0.2em] mb-4">{t.about.vision.title}</h3>
            <p className="text-[#0B1320] text-lg leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{t.about.vision.text}</p>
          </motion.div>
        </div>

        {/* Values */}
        <motion.div {...fadeIn()} className="mb-20">
          <h3 className="text-2xl font-bold text-[#0B1320] mb-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{t.about.values.title}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((v, i) => (
              <motion.div key={v.title} {...fadeIn(0.1 + i * 0.1)} className="p-6 border border-black/10 hover:border-[#FF6B00]/50 transition-colors group">
                <div className="w-12 h-12 bg-[#FF6B00]/10 flex items-center justify-center mb-4 group-hover:bg-[#FF6B00] transition-colors">
                  <v.icon size={24} className="text-[#FF6B00] group-hover:text-white transition-colors" weight="bold" />
                </div>
                <h4 className="text-lg font-semibold text-[#0B1320] mb-2">{v.title}</h4>
                <p className="text-sm text-[#475569] leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Leadership Team */}
        <motion.div {...fadeIn()} className="mb-20">
          <h3 className="text-2xl font-bold text-[#0B1320] mb-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{t.about.team.title}</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {t.about.team.members.map((member, i) => (
              <motion.div key={member.name} {...fadeIn(0.1 + i * 0.1)} className="bg-[#F8FAFC] border border-[#E2E8F0] p-6">
                <div className="w-16 h-16 bg-[#0B1320] flex items-center justify-center mb-4 text-white text-xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {member.name.split(' ').slice(1, 3).map(n => n[0]).join('')}
                </div>
                <h4 className="text-lg font-semibold text-[#0B1320] mb-1">{member.name}</h4>
                <p className="text-[#FF6B00] text-sm font-medium mb-3">{member.role}</p>
                <p className="text-sm text-[#475569] leading-relaxed">{member.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Methodology */}
        <motion.div {...fadeIn()} className="mb-20">
          <h3 className="text-2xl font-bold text-[#0B1320] mb-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{t.about.methodology.title}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.about.methodology.steps.map((step, i) => (
              <motion.div key={step.step} {...fadeIn(0.1 + i * 0.1)} className="relative p-6 bg-white border border-black/10 group hover:border-[#FF6B00]/30 transition-colors">
                <span className="text-5xl font-bold text-[#FF6B00]/10 absolute top-2 right-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{step.step}</span>
                <h4 className="text-base font-semibold text-[#0B1320] mb-2 mt-6">{step.title}</h4>
                <p className="text-sm text-[#475569] leading-relaxed">{step.desc}</p>
                {i < t.about.methodology.steps.length - 1 && (
                  <ArrowRight size={16} className="hidden lg:block absolute top-1/2 -right-3 text-[#FF6B00] z-10" weight="bold" />
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Certifications + Legal */}
        <div className="grid lg:grid-cols-2 gap-8">
          <motion.div {...fadeIn(0.1)} className="bg-[#F8FAFC] border border-[#E2E8F0] p-8">
            <div className="flex items-center gap-3 mb-6">
              <Certificate size={24} className="text-[#FF6B00]" weight="bold" />
              <h3 className="text-xl font-semibold text-[#0B1320]">{t.about.certifications.title}</h3>
            </div>
            <ul className="space-y-3">
              {t.about.certifications.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#475569]">
                  <span className="w-1.5 h-1.5 bg-[#FF6B00] rounded-full mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeIn(0.2)} className="bg-[#0B1320] p-8" data-testid="legal-info">
            <h3 className="text-xl font-semibold text-white mb-8">{t.about.legal.title}</h3>
            <div className="space-y-6">
              {[
                { label: t.about.legal.address, value: companyInfo.address },
                { label: t.about.legal.rccm, value: companyInfo.rccm, mono: true },
                { label: t.about.legal.national_id, value: companyInfo.nationalId, mono: true },
                { label: t.about.legal.tax_number, value: companyInfo.taxNumber, mono: true },
              ].map((item, i) => (
                <div key={i} className={i < 3 ? "border-b border-white/10 pb-6" : ""}>
                  <p className="text-xs text-white/50 uppercase tracking-[0.15em] mb-2">{item.label}</p>
                  <p className={`text-white font-medium ${item.mono ? 'font-mono text-sm' : ''}`}>{item.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 h-1 w-16 bg-[#FF6B00]" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
